import './App.css';
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import useMediaQuery from '@mui/material/useMediaQuery';

import { UserContext } from './context/UserContext';
import { getUserFromToken } from './api/authApi';

// Pages
import Login from './pages/Login';
import ChangePassword from './pages/ChangePassword';
import RequirePasswordChange from './components/RequirePasswordChange';
import SessionManager from './components/SessionManager';
import CompanySuper from './pages/company/companySuper';
import CompanyAdminDashboard from './pages/company/companyAdminDashboard';
import BranchCreation from './pages/company/BranchCreation';
import Status from './pages/company/Status';
import System from './pages/company/System';
import Event from './pages/company/Event';
import TicketCreation from './pages/company/TicketCreation';
import BenSuper from './pages/ben/BenSuper';
import BenAdmin from './pages/ben/BenAdmin';
import TeleSuper from './pages/etc/TeleSuper';
import Branch from './pages/company/Branch';
import TeleTech from './pages/etc/TeleTechnician';
import TechCreation from './pages/etc/TechnicianCreation';
import NtkStatus from './pages/etc/NtkStatus';
import TeleReport from './pages/etc/TeleReport';
import TeleTickets from './pages/etc/Tickets';
import TeleAdmin from './pages/etc/TeleAdmin';
import Reports from './pages/company/Report';

// Layout Components
import DashboardLayout from './components/layout/DashboardLayout';

// Icons
import HubIcon from "@mui/icons-material/Hub";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import DashboardIcon from "@mui/icons-material/Dashboard";
import InsertChartIcon from "@mui/icons-material/InsertChart";
import AssessmentIcon from "@mui/icons-material/Assessment";
import DnsIcon from "@mui/icons-material/Dns";

const theme = createTheme({
  palette: {
    primary: {
      main: '#FFD600',
    },
    secondary: {
      main: '#000000',
    },
  },
});

const companyMenu = [
  { name: "Dashboard", path: "/dashboard", icon: <DashboardIcon /> },
  { name: "Branches User", path: "/branchCreation", icon: <HubIcon /> },
  { name: "Branches Status", path: "/status", icon: <MonitorHeartIcon /> },
  { name: "System", path: "/system", icon: <DnsIcon /> },
  { name: "Recent Events", path: "/event", icon: <NotificationsActiveIcon /> },
  { name: "Tickets", path: "/ticketCreation", icon: <ConfirmationNumberIcon /> },
  { name: "Reports", path: "/report", icon: <InsertChartIcon /> },
];

const teleAdminMenu = [
  { name: "Technicians", path: "/teleTech", icon: <HubIcon /> },
  { name: "Network Status", path: "/ntkStatus", icon: <MonitorHeartIcon /> },
  { name: "Tickets", path: "/teleTickets", icon: <ConfirmationNumberIcon /> },
  { name: "Reports", path: "/teleReport", icon: <AssessmentIcon /> },
];

const getRoleRoute = (role) => {
  const routes = {
    BEN_SUPER: "/benSuper",
    BEN_ADMIN: "/benAdmin",
    TELE_SUPER: "/teleSuper",
    TELE_ADMIN: "/teleTech",
    COMPANY_SUPER: "/companySuper",
    COMPANY_ADMIN: "/dashboard",
    END_USER: "/branch",
    TELE_USER: "/teleUser",
  };
  return routes[role] || "/login";
};

function App() {
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem("user");
    const parsed = saved ? JSON.parse(saved) : null;
    const tokenUser = getUserFromToken();
    if (parsed && tokenUser?.mustChangePassword) {
      parsed.mustChangePassword = true;
    }
    return parsed;
  });
  const isMobile = useMediaQuery('(max-width:900px)');

  useEffect(() => {
    if (user) localStorage.setItem("user", JSON.stringify(user));
    else localStorage.removeItem("user");
  }, [user]);

  const PrivateRoute = ({ children, allowPasswordChange = false }) => {
    if (!user) return <Navigate to="/login" replace />;
    if (!allowPasswordChange) {
      return <RequirePasswordChange user={user}>{children}</RequirePasswordChange>;
    }
    return children;
  };

  const userRole = user?.role;

  // COMPANY_ADMIN Layout with Floating Sidebar
  if (user && userRole === 'COMPANY_ADMIN') {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <UserContext.Provider value={{ user, setUser }}>
            <SessionManager>
              <ToastContainer position="top-right" />
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/change-password" element={<PrivateRoute allowPasswordChange><ChangePassword /></PrivateRoute>} />
                <Route path="/" element={<DashboardLayout menu={companyMenu} title="Admin Dashboard" isMobile={isMobile} />}>
                  <Route index element={<Navigate to="/dashboard" replace />} />
                  <Route path="dashboard" element={<PrivateRoute><CompanyAdminDashboard /></PrivateRoute>} />
                  <Route path="branchCreation" element={<PrivateRoute><BranchCreation /></PrivateRoute>} />
                  <Route path="status" element={<PrivateRoute><Status /></PrivateRoute>} />
                  <Route path="system" element={<PrivateRoute><System /></PrivateRoute>} />
                  <Route path="event" element={<PrivateRoute><Event /></PrivateRoute>} />
                  <Route path="ticketCreation" element={<PrivateRoute><TicketCreation /></PrivateRoute>} />
                  <Route path="report" element={<PrivateRoute><Reports /></PrivateRoute>} />
                </Route>
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </SessionManager>
          </UserContext.Provider>
        </Router>
      </ThemeProvider>
    );
  }

  // TELE_ADMIN Layout with different menu and title
  if (user && userRole === 'TELE_ADMIN') {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <UserContext.Provider value={{ user, setUser }}>
            <SessionManager>
              <ToastContainer position="top-right" />
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/change-password" element={<PrivateRoute allowPasswordChange><ChangePassword /></PrivateRoute>} />
                <Route path="/" element={<DashboardLayout menu={teleAdminMenu} title="Telecom Admin Dashboard" isMobile={isMobile} />}>
                  <Route index element={<Navigate to="/teleTech" replace />} />
                  <Route path="teleTech" element={<PrivateRoute><TechCreation /></PrivateRoute>} />
                  <Route path="ntkStatus" element={<PrivateRoute><NtkStatus /></PrivateRoute>} />
                  <Route path="teleTickets" element={<PrivateRoute><TeleTickets /></PrivateRoute>} />
                  <Route path="teleReport" element={<PrivateRoute><TeleReport /></PrivateRoute>} />
                </Route>
                <Route path="*" element={<Navigate to="/teleTech" replace />} />
              </Routes>
            </SessionManager>
          </UserContext.Provider>
        </Router>
      </ThemeProvider>
    );
  }

  // Other roles
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <UserContext.Provider value={{ user, setUser }}>
          <SessionManager>
            <ToastContainer />
            <Routes>
              <Route path="/" element={<Login />} />
              <Route path="/login" element={<Login />} />
              <Route path="/change-password" element={<PrivateRoute allowPasswordChange><ChangePassword /></PrivateRoute>} />
              <Route path="/benSuper" element={<PrivateRoute><BenSuper /></PrivateRoute>} />
              <Route path="/benAdmin" element={<PrivateRoute><BenAdmin /></PrivateRoute>} />
              <Route path="/teleSuper" element={<PrivateRoute><TeleSuper /></PrivateRoute>} />
              <Route path="/branch" element={<PrivateRoute><Branch /></PrivateRoute>} />
              <Route path="/companySuper" element={<PrivateRoute><CompanySuper /></PrivateRoute>} />
              <Route path="*" element={<Navigate to={user ? getRoleRoute(userRole) : "/login"} replace />} />
            </Routes>
          </SessionManager>
        </UserContext.Provider>
      </Router>
    </ThemeProvider>
  );
}

export default App;